/**
 * How a responsibility area looks, from what the server stores (RESP2c).
 *
 * Until RESP2b the label, icon and colour of an area lived in a static
 * frontend table keyed by the 13 codes. That stopped working the moment
 * an organisation could create a fourteenth: a custom area has no entry
 * there and would render nameless and blank.
 *
 * So the source is the row now. The server stores an ICON KEY (a lucide
 * component name) and a COLOUR TOKEN (the Tailwind badge classes), both
 * validated server-side against exactly the set below — see
 * `app/services/responsibility_area_presentation.py`, which derives its
 * allowlists from the same 13 definitions this file imports its
 * components for.
 *
 * **The colour needs no map.** The token IS the class string, which is
 * why it survives a theme switch: a stored hex would only ever be right
 * in the theme it was picked in, and that is the reason CAT1 chose
 * tokens in the first place.
 *
 * **The icon does need one.** A component cannot be stored, so the row
 * carries its name and this resolves it. A name outside the map renders
 * the neutral fallback rather than nothing — the server rejects unknown
 * keys, so reaching the fallback means the two lists have drifted, and a
 * visible generic tile is a better way to find that out than a blank.
 */
import {
  Banknote,
  Boxes,
  Briefcase,
  Building2,
  ClipboardList,
  FileSignature,
  Home,
  KeyRound,
  Megaphone,
  Search,
  Sparkles,
  Tag,
  Users,
  Wrench,
} from "lucide-react";

/**
 * Name → component, for every key the backend allows.
 *
 * Mirrors `ALLOWED_ICONS`. Adding one here is not enough to make it
 * selectable — the server has to allow it too, and it derives its list
 * from the system definitions.
 */
export const AREA_ICONS = {
  Banknote,
  Boxes,
  Briefcase,
  Building2,
  ClipboardList,
  FileSignature,
  Home,
  KeyRound,
  Megaphone,
  Search,
  Sparkles,
  Users,
  Wrench,
};

/** Neutral tile for an area whose icon key is unknown or unset. */
export const FALLBACK_ICON = Tag;

/** The same neutral pair the backend uses as its create-time default. */
export const FALLBACK_COLOR_TOKEN =
  "border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-300";

/** The icon component for a stored key. Never throws, never returns null. */
export function areaIcon(iconKey) {
  return AREA_ICONS[iconKey] || FALLBACK_ICON;
}

/** The class string for a stored colour token, or the neutral one. */
export function areaColorToken(token) {
  return token || FALLBACK_COLOR_TOKEN;
}

/**
 * The icon keys a picker may offer, in a stable order.
 *
 * Sorted rather than insertion-ordered so the grid reads the same on
 * every render and in every test.
 */
export const SELECTABLE_ICONS = Object.keys(AREA_ICONS).sort();

/**
 * The colour tokens a picker may offer.
 *
 * Written out rather than derived from the areas currently loaded: an
 * organisation that has only ever used three colours should still be
 * offered all of them. Kept in the same order as the system areas so the
 * palette reads as a spectrum rather than a list.
 */
export const SELECTABLE_COLOR_TOKENS = [
  "border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-300",
  "border-indigo-500/30 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300",
  "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  "border-pink-500/30 bg-pink-500/10 text-pink-700 dark:text-pink-300",
  "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  "border-teal-500/30 bg-teal-500/10 text-teal-700 dark:text-teal-300",
  "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  "border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-300",
  "border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300",
  "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300",
  "border-lime-500/30 bg-lime-500/10 text-lime-700 dark:text-lime-300",
  "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300",
];

/** A readable colour name for the picker's accessible label. */
export function colorTokenName(token) {
  const match = /bg-([a-z]+)-500/.exec(token || "");
  const german = {
    slate: "Grau",
    indigo: "Indigo",
    emerald: "Grün",
    pink: "Pink",
    sky: "Himmelblau",
    violet: "Violett",
    teal: "Türkis",
    amber: "Bernstein",
    orange: "Orange",
    cyan: "Cyan",
    blue: "Blau",
    lime: "Limette",
    rose: "Rosé",
  };
  return german[match?.[1]] || "Farbe";
}

/**
 * How an area's default people should be DESCRIBED — not what they are.
 *
 * Vantio has no team model, and RESP2c does not invent one. What this
 * returns is a presentation type for a configuration that happens to
 * name several people: "Team" is the word on the badge, never an entity,
 * never an id. The moment it returned an object with a team id it would
 * be describing software that does not exist.
 *
 * @returns {"person"|"team"|"open"}
 */
export function assignmentKind(area) {
  // RESP2c.1: counted through a Set, not by adding two lengths. The old
  // arithmetic assumed the lead was never also in the participants —
  // true for anything the edit form writes, because the multi-picker
  // excludes them, and not guaranteed for rows that predate that rule or
  // arrive from anywhere else. The symptom was an area with one human
  // being labelled "Team", right next to that person's single name.
  //
  // Works on the ID arrays because that is what every caller has; the
  // same deduplication on the expanded objects lives in
  // ``buildAreaPeople``, and the two agree by construction because they
  // agree on what "the same person twice" means.
  const ids = new Set();
  if (area?.default_assigned_to_user_id) {
    ids.add(String(area.default_assigned_to_user_id));
  }
  for (const id of area?.participant_user_ids || []) {
    if (id != null && id !== "") ids.add(String(id));
  }
  if (ids.size === 0) return "open";
  return ids.size > 1 ? "team" : "person";
}

/** German badge text for `assignmentKind`. */
export function assignmentKindLabel(kind) {
  switch (kind) {
    case "team":
      return "Team";
    case "person":
      return "Person";
    default:
      return "Offen";
  }
}
